const CANDIDATE_LINE = /^a=candidate:\S+ (\d+) \S+ \d+ (\S+) (\d+) typ \S+/;

const mediaPort = (line: string): number | null => {
    const match = /^m=\S+ (\d+)\s/.exec(line);
    if (!match) return null;
    const port = Number(match[1]);
    return Number.isFinite(port) ? port : null;
};

const connectionAddress = (lines: string[]): string | null => {
    for (const line of lines) {
        const match = /^c=IN IP[46] (\S+)/.exec(line);
        if (match) return match[1];
    }
    return null;
};

const selectedDestination = (address: string | null, port: number | null): {address: string; port: number} | null => {
    if (!address || address === '0.0.0.0' || address === '::' || address === '::0') return null;
    if (port == null || port <= 0) return null;
    return {address, port};
};

const candidateMatches = (line: string, address: string, port: number): boolean => {
    const match = CANDIDATE_LINE.exec(line);
    if (!match) return true;
    const [, component, candidateAddress, candidatePort] = match;
    return component === '1' && candidateAddress === address && Number(candidatePort) === port;
};

const splitMediaBlocks = (lines: string[]): string[][] => {
    const blocks: string[][] = [];
    let current: string[] = [];
    lines.forEach((line) => {
        if (line.startsWith('m=') && current.length > 0) {
            blocks.push(current);
            current = [line];
            return;
        }
        current.push(line);
    });
    if (current.length > 0) {
        blocks.push(current);
    }
    return blocks;
};

// Chrome writes every gathered relay into localDescription before JsSIP copies it
// into the INVITE. c= and the m= port are the destination the call actually uses.
// Other a=candidate lines are unused TURN relays and must not be offered.
export const keepSelectedIceCandidates = (sdp: string): string => {
    if (!sdp.includes('a=candidate:')) return sdp;

    const newline = sdp.includes('\r\n') ? '\r\n' : '\n';
    const lines = sdp.split(/\r\n|\n|\r/);
    const blocks = splitMediaBlocks(lines);
    const sessionBlock = blocks[0]?.[0]?.startsWith('m=') ? null : blocks[0];
    const sessionAddress = sessionBlock ? connectionAddress(sessionBlock) : null;
    let dropped = 0;

    const nextBlocks = blocks.map((block) => {
        if (!block[0]?.startsWith('m=')) return block;
        const port = mediaPort(block[0]);
        const address = connectionAddress(block) ?? sessionAddress;
        const selected = selectedDestination(address, port);
        if (!selected) return block;

        const candidates = block.filter((line) => line.startsWith('a=candidate:'));
        if (candidates.length <= 1) return block;

        const kept = block.filter((line) => {
            if (!line.startsWith('a=candidate:')) return true;
            const matches = candidateMatches(line, selected.address, selected.port);
            if (!matches) dropped += 1;
            return matches;
        });
        if (!kept.some((line) => line.startsWith('a=candidate:'))) {
            dropped -= candidates.length;
            return block;
        }
        return kept;
    });

    if (dropped === 0) return sdp;
    return nextBlocks.flat().join(newline);
};

export const attachLocalSdpCandidateFilter = (session: {on?: Function} | null | undefined): void => {
    if (!session || typeof session.on !== 'function' || (session as any).__selectedCandidateFilter) {
        return;
    }
    (session as any).__selectedCandidateFilter = true;

    session.on('sdp', (event: {originator?: string; sdp?: string}) => {
        if (event?.originator !== 'local' || typeof event.sdp !== 'string') return;
        const next = keepSelectedIceCandidates(event.sdp);
        if (next === event.sdp) return;
        console.log('[ICE] dropped unused candidates from local SDP');
        event.sdp = next;
    });
};
